/**
 * Edit-mode save path: turn the raw serialized HTML the sandboxed viewer
 * iframe posts back (which still carries every runtime-injected artifact +
 * contenteditable plumbing) into a clean HTML document safe to POST as a new
 * report version. The central-api sanitizer re-sanitizes on top of this; this
 * strip exists so we never persist our own scaffolding into the stored report.
 *
 * Structural-integrity guarantee: the viewer only ever makes DEEPEST text
 * leaves editable via contenteditable="plaintext-only" (see viewer-runtime.ts
 * setupEditMode), so the serialized DOM has the SAME element tree as the
 * loaded version — only text runs differ. This module therefore never needs to
 * (and never does) rewrite element structure; it only removes injected
 * attributes/nodes. That invariant is what report-edit-serialize.test.ts
 * proves in jsdom.
 */

/** Attributes injected by the runtime that must never be persisted. */
const TARA_ATTRS = ['contenteditable', 'spellcheck'];

function isTaraDataAttr(name: string): boolean {
  return name.toLowerCase().indexOf('data-tara-') === 0;
}

/**
 * Remove every runtime-injected artifact from `doc` in place:
 *   - <script> nodes (the injected runtime),
 *   - runtime-injected <style data-tara-*> (runtime, fonts, report colour
 *     vars) and the CSP <meta http-equiv>,
 *   - the deep-dive expand shells (unwrapped back to the original block),
 *   - contenteditable / spellcheck / data-tara-* attributes.
 */
export function stripInjectedArtifacts(doc: Document): void {
  const root = doc.documentElement;
  if (!root) return;

  // 1. Injected scripts + CSP meta + tagged runtime styles.
  root.querySelectorAll('script').forEach((el) => el.remove());
  root
    .querySelectorAll('meta[http-equiv="Content-Security-Policy"]')
    .forEach((el) => el.remove());
  //    The report-vars block is the agency colour-token :root rule the preview
  //    injects (build-srcdoc); saving it would pin today's branding into the
  //    stored version and shadow any later branding change.
  root
    .querySelectorAll(
      'style[data-tara-runtime], style[data-tara-fonts], style[data-tara-report-vars]'
    )
    .forEach((el) => el.remove());

  // 2. Unwrap any deep-dive expand shells back to their original block. The
  //    original [data-expandable] block lives inside `.tara-expand__content`;
  //    lift it out to where the shell sits and drop the toggle chrome. (Edit
  //    mode does not inject shells, but this keeps the strip correct for any
  //    serialized DOM that happens to contain them.)
  root.querySelectorAll('[data-tara-shell]').forEach((shell) => {
    const block = shell.querySelector('[data-expandable]');
    const parent = shell.parentNode;
    if (block && parent) {
      parent.replaceChild(block, shell);
    } else if (parent) {
      parent.removeChild(shell);
    }
  });

  // 3. Attribute scrub across every remaining element.
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  let node = walker.currentNode as Element | null;
  const toClean: Element[] = [];
  while (node) {
    toClean.push(node);
    node = walker.nextNode() as Element | null;
  }
  for (const el of toClean) {
    const attrs = Array.from(el.attributes ?? []);
    for (const attr of attrs) {
      const name = attr.name.toLowerCase();
      if (TARA_ATTRS.includes(name) || isTaraDataAttr(name)) {
        el.removeAttribute(attr.name);
      }
    }
  }
}

/**
 * Parse the raw serialized HTML from the viewer, strip injected artifacts, and
 * return a clean `<!doctype html>…` string ready to POST as a new version.
 */
export function serializeCleanHtml(rawHtml: string): string {
  const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
  stripInjectedArtifacts(doc);
  return `<!doctype html>${doc.documentElement.outerHTML}`;
}
